import { useCallback, useEffect, useRef, useState } from "react";
import type { CityDetailResponseDto, MapResponseDto } from "../../shared/api";

interface ResourceState<T> {
  data: T | null;
  loading: boolean;
  stale: boolean;
  error: string | null;
}

export function useCityMap(selectedCode: string | null) {
  const [map, setMap] = useState<ResourceState<MapResponseDto>>({
    data: null,
    loading: true,
    stale: false,
    error: null
  });
  const [city, setCity] = useState<ResourceState<CityDetailResponseDto>>({
    data: null,
    loading: false,
    stale: false,
    error: null
  });
  const mapEtag = useRef<string | undefined>(undefined);
  const cityEtags = useRef(new Map<string, string>());
  const cityCache = useRef(new Map<string, CityDetailResponseDto>());

  const refreshMap = useCallback(async () => {
    setMap((current) => ({ ...current, loading: !current.data, error: null }));
    try {
      const response = await fetch("/api/map", {
        headers: mapEtag.current ? { "If-None-Match": mapEtag.current } : undefined
      });
      if (response.status === 304) {
        setMap((current) => ({ ...current, loading: false, stale: false }));
        return;
      }
      if (!response.ok) throw new Error(`Harita isteği başarısız (${response.status})`);
      const payload = (await response.json()) as MapResponseDto;
      const etag = response.headers.get("ETag");
      if (etag) mapEtag.current = etag;
      setMap({ data: payload, loading: false, stale: false, error: null });
    } catch (error) {
      setMap((current) => ({
        ...current,
        loading: false,
        stale: Boolean(current.data),
        error: error instanceof Error ? error.message : "Harita şu anda yenilenemiyor."
      }));
    }
  }, []);

  const refreshCity = useCallback(async (code: string) => {
    const cachedCity = cityCache.current.get(code) ?? null;
    setCity((current) => ({
      ...current,
      data: current.data?.code === code ? current.data : cachedCity,
      loading: current.data?.code !== code && !cachedCity,
      error: null
    }));
    try {
      const etag = cityEtags.current.get(code);
      let response = await fetch(`/api/cities/${code}`, {
        headers: etag ? { "If-None-Match": etag } : undefined
      });
      if (response.status === 304) {
        const cached = cityCache.current.get(code);
        if (cached) {
          setCity({ data: cached, loading: false, stale: false, error: null });
          return true;
        }
        cityEtags.current.delete(code);
        response = await fetch(`/api/cities/${code}`);
      }
      if (!response.ok) throw new Error(`Şehir isteği başarısız (${response.status})`);
      const payload = (await response.json()) as CityDetailResponseDto;
      const nextEtag = response.headers.get("ETag");
      if (nextEtag) cityEtags.current.set(code, nextEtag);
      cityCache.current.set(code, payload);
      setCity({ data: payload, loading: false, stale: false, error: null });
      setMap((current) =>
        current.data
          ? {
              ...current,
              data: {
                ...current.data,
                cities: current.data.cities.map((item) =>
                  item.code === payload.code ? { ...item, ...payload } : item
                )
              }
            }
          : current
      );
      return true;
    } catch (error) {
      setCity((current) => ({
        ...current,
        loading: false,
        stale: Boolean(current.data),
        error: error instanceof Error ? error.message : "Şehir şu anda yenilenemiyor."
      }));
      return false;
    }
  }, []);

  useEffect(() => {
    void refreshMap();
    const onFocus = () => void refreshMap();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refreshMap]);

  useEffect(() => {
    if (!selectedCode) {
      setCity({ data: null, loading: false, stale: false, error: null });
      return;
    }
    void refreshCity(selectedCode);
    const interval = window.setInterval(() => void refreshCity(selectedCode), 30_000);
    return () => window.clearInterval(interval);
  }, [refreshCity, selectedCode]);

  return { map, city, refreshMap, refreshCity };
}
